/**
 * AdminFlagged — review flagged Supporter Wall messages.
 *
 * Reads reports from the shared session-scoped store (`lib/wallReports.js`).
 * Pending reports (resolved = null) show up in the main table with two
 * actions:
 *
 *   Approve   → clears the flag; the message stays public on the wall.
 *   Remove    → hides the message from the public wall permanently
 *              (session-scoped for now — will be a real hard-delete once
 *              backend lands).
 *
 * Resolved history sits under a small toggle so operators can see the last
 * few decisions without cluttering the main queue.
 *
 * TODO(backend): swap the store for real endpoints — see lib/wallReports.js.
 */

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Flag, Check, Trash2, MessagesSquare, ChevronDown, ChevronUp, ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";
import { SectionHeader, AdminButton, EmptyState, StatusBadge } from "./_ui";
import {
  getReports, resolveReport, subscribeReports, REPORT_REASONS,
} from "../../lib/wallReports";

const relTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const reasonLabel = (key) =>
  (REPORT_REASONS.find((r) => r.key === key) || { label: key }).label;

const REASON_STYLE = {
  harassment: "bg-scarlet/15 text-scarlet ring-scarlet/30",
  hate:       "bg-scarlet/15 text-scarlet ring-scarlet/30",
  spam:       "bg-tangerine/15 text-tangerine ring-tangerine/30",
  other:      "bg-cobalt/15 text-cobalt ring-cobalt/30",
};

const ReasonChip = ({ reason }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ring-1 ring-inset ${
      REASON_STYLE[reason] || "bg-ink/10 text-ink ring-ink/20"
    }`}
    data-testid={`reason-chip-${reason}`}
  >
    {reasonLabel(reason)}
  </span>
);

export const AdminFlagged = () => {
  const [store, setStore] = useState(getReports());
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => subscribeReports(setStore), []);

  const records = useMemo(() => Object.values(store), [store]);
  const pending  = records.filter((r) => !r.resolved);
  const resolved = records
    .filter((r) => r.resolved)
    .sort((a, b) => new Date(b.resolved.at) - new Date(a.resolved.at));

  const onApprove = (r) => {
    resolveReport(r.id, "approved");
    toast.success("Message kept", {
      description: `"${r.authorName}"'s note stays visible on ${r.studentFirstName}'s wall.`,
    });
  };
  const onRemove = (r) => {
    resolveReport(r.id, "removed");
    toast(`Message removed`, {
      description: `Hidden from ${r.studentFirstName}'s public wall.`,
    });
  };

  return (
    <div className="space-y-10">
      <SectionHeader
        eyebrow="Flagged messages"
        title="Review notes reported by the community."
        description="Every reported note stays visible on the campaign until you make a call. Approve keeps it public; Remove hides it from the wall."
      />

      {pending.length === 0 ? (
        <EmptyState
          title="Queue clear"
          description="No pending flagged messages. When a visitor reports a Supporter Wall note, it will appear here."
        />
      ) : (
        <ul className="space-y-4" data-testid="flagged-pending-list">
          {pending.map((r) => {
            const lastReport = r.reports[r.reports.length - 1];
            return (
              <li
                key={r.id}
                className="overflow-hidden rounded-lg border border-tangerine/30 bg-sand"
                data-testid={`flagged-row-${r.id}`}
              >
                <div className="flex items-center justify-between gap-3 border-b border-tangerine/20 bg-tangerine/[0.06] px-5 py-3">
                  <div className="flex items-center gap-2">
                    <Flag className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
                    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-tangerine">
                      Reported · {r.reports.length} {r.reports.length === 1 ? "flag" : "flags"}
                    </span>
                    <span className="text-[11px] text-ink/60">· {relTime(lastReport.at)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Array.from(new Set(r.reports.map((rr) => rr.reason))).map((rk) => (
                      <ReasonChip key={rk} reason={rk} />
                    ))}
                  </div>
                </div>

                <div className="px-5 py-5">
                  <div className="flex items-baseline gap-3">
                    <span className="font-serif text-lg italic text-ink">{r.authorName}</span>
                    <span className="text-[11px] uppercase tracking-wide text-ink/50">
                      {relTime(r.postedAt)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-ink">
                    {r.message}
                  </p>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/demo/campaign/${r.campaignId}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                      data-testid={`view-context-${r.id}`}
                    >
                      View on {r.studentFirstName}'s campaign
                      <ExternalLink className="h-3 w-3" strokeWidth={2} />
                    </Link>
                    <div className="ml-auto flex items-center gap-2">
                      <AdminButton
                        variant="approve"
                        size="sm"
                        onClick={() => onApprove(r)}
                        testid={`approve-${r.id}`}
                      >
                        <Check className="h-3 w-3" strokeWidth={2} /> Keep public
                      </AdminButton>
                      <AdminButton
                        variant="reject"
                        size="sm"
                        onClick={() => onRemove(r)}
                        testid={`remove-${r.id}`}
                      >
                        <Trash2 className="h-3 w-3" strokeWidth={2} /> Remove
                      </AdminButton>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Resolved history — collapsed by default */}
      {resolved.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory((v) => !v)}
            className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60 hover:text-ink"
            data-testid="toggle-history"
          >
            {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Recent decisions ({resolved.length})
          </button>
          {showHistory && (
            <ul className="mt-4 divide-y divide-ink/10 overflow-hidden rounded-lg border border-ink/10 bg-sand" data-testid="flagged-history-list">
              {resolved.map((r) => (
                <li key={r.id} className="flex items-center gap-4 px-5 py-3">
                  <MessagesSquare className="h-3.5 w-3.5 shrink-0 text-ink/40" strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-ink">
                      <span className="font-medium">{r.authorName}</span>
                      <span className="text-ink/60"> · {r.studentFirstName}'s campaign</span>
                    </p>
                    <p className="mt-0.5 truncate text-xs text-ink/60">"{r.message}"</p>
                  </div>
                  <StatusBadge status={r.resolved.action === "approved" ? "approved" : "rejected"} />
                  <span className="text-[10px] uppercase tracking-wide text-ink/50">{relTime(r.resolved.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
